// src/components/common/UserDropdown.js
import React, { useState, useRef, useEffect } from 'react';
import { User, Settings, LogOut, Moon, Sun, CreditCard, HelpCircle, ChevronRight, FileText, Scale, ClipboardList } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocation, useNavigate } from 'react-router-dom';

const UserDropdown = ({ user, onLogout, isAdmin = false, accessMode = 'admin' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const dropdownRef = useRef(null);
    const helpRef = useRef(null);
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();

    // Determine the current portal based on URL
    const currentPortal = location.pathname.startsWith('/admin') ? 'admin'
                        : location.pathname.startsWith('/manager') ? 'manager'
                        : 'employee';

    // Close help submenu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (helpRef.current && !helpRef.current.contains(event.target)) {
                setHelpOpen(false);
            }
        };
        if (helpOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [helpOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        setIsOpen(false);
        onLogout();
    };

    const handleThemeToggle = () => {
        toggleTheme();
        // Don't close dropdown on theme toggle
    };

    // Determine links based on current portal and access mode
    const profileLink = currentPortal === 'admin' ? '/admin/profile'
                      : currentPortal === 'manager' ? '/manager/profile'
                      : '/employee/profile';

    // When in admin portal:
    // - "Admin Access" mode (accessMode === 'admin') → Company Settings (/admin/settings)
    // - "My Access" mode (accessMode === 'my') → Personal Settings (/admin/my-settings)
    const settingsLink = currentPortal === 'admin'
                       ? (accessMode === 'my' ? '/admin/my-settings' : '/admin/settings')
                       : currentPortal === 'manager' ? '/manager/settings'
                       : '/employee/settings';

    const bgColor = currentPortal === 'admin' ? 'bg-indigo-600' : 'bg-blue-600';

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`w-10 h-10 ${bgColor} rounded-full flex items-center justify-center text-white font-semibold hover:opacity-90 transition-opacity`}
            >
                {user?.first_name?.[0]}{user?.last_name?.[0]}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-2 z-50 border dark:border-gray-700">
                    <div className="px-4 py-2 border-b dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                    </div>

                    {/* Theme Toggle */}
                    <div className="px-4 py-3 border-b dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Theme
                            </span>
                            <button
                                onClick={handleThemeToggle}
                                className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 dark:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            >
                                <span className="sr-only">Toggle theme</span>
                                <span
                                    className={`${
                                        theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
                                    } inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform flex items-center justify-center`}
                                >
                                    {theme === 'dark' ? (
                                        <Moon className="w-2.5 h-2.5 text-gray-700" />
                                    ) : (
                                        <Sun className="w-2.5 h-2.5 text-yellow-500" />
                                    )}
                                </span>
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {theme === 'dark' ? 'Dark mode' : 'Light mode'}
                        </p>
                    </div>

                    {/* Only show My Profile when NOT in admin portal OR when in manager/employee portal */}
                    {currentPortal !== 'admin' && (
                        <a
                            href={profileLink}
                            className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            onClick={() => setIsOpen(false)}
                        >
                            <User className="w-4 h-4 mr-2"/>
                            My Profile
                        </a>
                    )}

                    {/* Billing & Help - only in admin portal */}
                    {currentPortal === 'admin' && (
                        <>
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate('/admin/billing');
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <CreditCard className="w-4 h-4 mr-2"/>
                                Billing
                            </button>

                            {/* Help with expandable submenu */}
                            <div className="relative" ref={helpRef}>
                                <button
                                    onClick={() => setHelpOpen(!helpOpen)}
                                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                    <div className="flex items-center">
                                        <HelpCircle className="w-4 h-4 mr-2"/>
                                        Help
                                    </div>
                                    <ChevronRight className={`w-4 h-4 transition-transform ${helpOpen ? 'rotate-90' : ''}`}/>
                                </button>

                                {/* Help Submenu - expands inline */}
                                {helpOpen && (
                                    <div className="pl-6 pr-2 py-1 space-y-1 bg-gray-50 dark:bg-gray-700/30">
                                        <button
                                            onClick={() => {
                                                setHelpOpen(false);
                                                setIsOpen(false);
                                                navigate('/admin/help');
                                            }}
                                            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                        >
                                            <HelpCircle className="w-4 h-4 mr-2"/>
                                            Help Center
                                        </button>
                                        <button
                                            onClick={() => {
                                                setHelpOpen(false);
                                                setIsOpen(false);
                                                navigate('/admin/privacy');
                                            }}
                                            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                        >
                                            <FileText className="w-4 h-4 mr-2"/>
                                            Privacy Policy
                                        </button>
                                        <button
                                            onClick={() => {
                                                setHelpOpen(false);
                                                setIsOpen(false);
                                                navigate('/admin/terms');
                                            }}
                                            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                        >
                                            <ClipboardList className="w-4 h-4 mr-2"/>
                                            Terms & Conditions
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <a
                        href={settingsLink}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsOpen(false)}
                    >
                        <Settings className="w-4 h-4 mr-2"/>
                        Settings
                    </a>

                    <hr className="my-2 dark:border-gray-700"/>

                    <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                        <LogOut className="w-4 h-4 mr-2"/>
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserDropdown;
